/**
 * Share Link Management
 * 
 * Handles encoding/decoding group data in URLs for sharing.
 * Following dev guide: client-side capability, URL-based state.
 */

import pako from 'pako';
import { Group } from './groupModel';
import { validateGroup } from './validation';

// Maximum URL length for safety (most browsers support 2048+)
const MAX_URL_LENGTH = 2000;

/**
 * Encode group data into a URL-safe string
 */
export function encodeGroupForUrl(group: Group): string {
  try {
    // Create a clean copy of the group without computed fields
    const groupForSharing: Group = {
      ...group,
      balances: undefined, // Will be recalculated
      settlements: undefined // Will be recalculated
    };

    // Convert to JSON
    const jsonString = JSON.stringify(groupForSharing);
    
    // Compress the JSON
    const compressed = pako.deflate(jsonString);
    
    // Convert to base64 URL-safe string
    const base64 = btoa(String.fromCharCode(...compressed))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
    
    return base64;
  } catch (error) {
    throw new Error('Failed to encode group data for sharing');
  }
}

/**
 * Decode group data from URL string
 */
export function decodeGroupFromUrl(encodedData: string): Group {
  try {
    // Convert base64 URL-safe back to normal base64
    const base64 = encodedData
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    // Add padding if needed
    const padded = base64 + '==='.slice(0, (4 - base64.length % 4) % 4);
    
    // Convert from base64 to binary
    const binaryString = atob(padded);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    // Decompress
    const jsonString = pako.inflate(bytes, { to: 'string' });
    
    // Parse JSON
    const groupData = JSON.parse(jsonString);
    
    // Validate the group data
    const validatedGroup = validateGroup(groupData);
    
    return validatedGroup;
  } catch (error) {
    throw new Error('Failed to decode group data from link. The link may be corrupted or invalid.');
  }
}

/**
 * Generate a shareable URL with group data
 */
export function generateShareUrl(group: Group): string {
  try {
    const encodedGroup = encodeGroupForUrl(group);
    
    // Check if we can fit in URL
    const baseUrl = `${window.location.origin}${window.location.pathname}#/group/${group.id}`;
    const urlWithData = `${baseUrl}?data=${encodedGroup}`;
    
    if (urlWithData.length > MAX_URL_LENGTH) {
      // Fallback: Generate URL without data (will work only for creator)
      console.warn('Group data too large for URL sharing. Using ID-only link.');
      return baseUrl;
    }
    
    return urlWithData;
  } catch (error) {
    console.error('Failed to generate share URL:', error);
    // Fallback: Generate simple ID-only URL
    return `${window.location.origin}${window.location.pathname}#/group/${group.id}`;
  }
}

/**
 * Extract group data from current URL if present
 */
export function extractGroupFromUrl(): Group | null {
  try {
    const urlParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const encodedData = urlParams.get('data');
    
    if (!encodedData) {
      return null;
    }
    
    return decodeGroupFromUrl(encodedData);
  } catch (error) {
    console.error('Failed to extract group from URL:', error);
    return null;
  }
}

/**
 * Check if current URL has group data
 */
export function hasGroupDataInUrl(): boolean {
  const urlParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
  return urlParams.has('data');
}

/**
 * Get group ID from URL path
 */
export function getGroupIdFromUrl(): string | null {
  const hash = window.location.hash;
  const match = hash.match(/#\/group\/([^?]+)/);
  return match ? match[1] : null;
}

/**
 * Clean URL by removing group data (for privacy)
 */
export function cleanUrl(): void {
  const hash = window.location.hash.split('?')[0];
  if (hash !== window.location.hash) {
    window.history.replaceState({}, '', window.location.pathname + hash);
  }
}

/**
 * Estimate encoded size of group data
 */
export function estimateGroupDataSize(group: Group): number {
  try {
    const encoded = encodeGroupForUrl(group);
    const fullUrl = `${window.location.origin}${window.location.pathname}#/group/${group.id}?data=${encoded}`;
    return fullUrl.length;
  } catch (error) {
    return Infinity;
  }
}

/**
 * Check if group is suitable for URL sharing
 */
export function canShareViaUrl(group: Group): boolean {
  return estimateGroupDataSize(group) <= MAX_URL_LENGTH;
}
